/* eslint-disable no-nested-ternary */
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { getAPICall } from "lib/callApi";
import { GET_LEADERBOARD_SELECTION_OPTIONS, THEMES } from "lib/constants";
import React, { useEffect, useState } from "react";
import "react-grid-layout/css/styles.css";
import { useSelector } from "react-redux";
import "react-resizable/css/styles.css";
import {
  fetchDateRangeForWidget,
  formatDatesForWidget,
  handlePeriodsForWidget,
  numberFormatter,
} from "utils/HelperFunctions";
import TooltipLight from "utils/TooltipLight";
import "../styles/widget.scss";
import CustomizeLeaderboardModal from "./CustomizeLeaderboardModal";

export default function Leaderboard({ selectedUser, widget, updateWidgetMetadata }) {
  const { refresh: refreshLeaderboard, kpiActionId: refreshKpi } = useSelector(
    (state) => state.home.leaderboardWidgets
  );
  const { dataSource, widgetTitle, isCustomizable, kpiId, kpiName, title, timePeriod, teams } =
    widget;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [periodDates, setPeriodDates] = useState({});
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [widgetOptions, setWidgetOptions] = useState({
    kpiId,
    kpiName,
    title: title || "Leaderboard",
    timePeriod: timePeriod || "monthly",
    teams: teams || [],
  });
  const [selectionListOptions, setSelectionListOptions] = useState({
    kpiOptions: [],
    teamOptions: [],
  });
  const [open, setOpen] = useState(false);

  const podiumColors = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32", 4: THEMES.PRIMARY.BLUE };
  const podiumBorderColors = { 1: "#F0CA00", 2: "#AFAFAF", 3: "#BE742B", 4: "#6E8BB9" };

  const handleCloseModal = () => setOpen(false);
  const handleOpenModal = async () => {
    const result = await getAPICall(GET_LEADERBOARD_SELECTION_OPTIONS, true);
    if (result?.data?.success) {
      setSelectionListOptions(result.data.data);
      setOpen(true);
    }
  };
  const handleModalSave = (options) => {
    setWidgetOptions(options);
    setPeriodDates(fetchDateRangeForWidget(options.timePeriod, loginUser.fiscalYearStart));
    updateWidgetMetadata(widget.gridKey, options);
  };

  const handlePeriods = (type) => {
    if (!periodDates.start && !periodDates.end) return;
    const dates = handlePeriodsForWidget(type, widgetOptions.timePeriod, periodDates);
    if (
      dates.start &&
      dates.end &&
      dates.start !== periodDates.start &&
      dates.end !== periodDates.end
    ) {
      setPeriodDates(dates);
    }
  };

  const fetchLeaderboardData = async () => {
    if (dataSource && periodDates.start && periodDates.end) {
      let payload = {
        start: periodDates.start,
        end: periodDates.end,
      };
      if (isCustomizable) {
        if (widgetOptions.kpiId === undefined) {
          return;
        }
        let filterTeams = [0]; // 0 indicates all teams
        if (widgetOptions.teams && widgetOptions.teams.length) {
          filterTeams = widgetOptions.teams;
        }
        payload = { ...payload, kpiId: widgetOptions.kpiId, teams: filterTeams };
      }
      const result = await getAPICall(dataSource, true, payload);
      if (result?.data?.success) {
        setLeaderboardData(result?.data?.data || []);
      }
    }
  };

  // const getAvatarName = (userName) => {
  //   try {
  //     if (userName.length <= 2) return userName;
  //     return userName
  //       .split(" ")
  //       .map((item) => item[0])
  //       .join("");
  //   } catch {
  //     return userName;
  //   }
  // };

  const getAvatarColor = (rank) => (rank > 3 ? podiumColors[4] : podiumColors[rank]);
  const getAvatarBorderColor = (rank) =>
    rank > 3 ? podiumBorderColors[4] : podiumBorderColors[rank];
  const getUserNameColor = (userId) => (userId === selectedUser ? "#4daff4" : "#888");

  useEffect(() => {
    if (!refreshLeaderboard) return;
    if (widgetOptions?.kpiId === refreshKpi) {
      fetchLeaderboardData();
    }
  }, [refreshLeaderboard]);

  useEffect(() => {
    fetchLeaderboardData();
  }, [periodDates, widgetOptions]);

  useEffect(() => {
    if (dataSource && dataSource.indexOf("year") >= 0) {
      setWidgetOptions({ ...widgetOptions, timePeriod: "yearly" });
    }
    const dates = fetchDateRangeForWidget(widgetOptions.timePeriod, loginUser.fiscalYearStart);
    setPeriodDates(dates);
  }, []);

  return (
    <>
      <Box
        p={2}
        sx={{
          overflowY: "auto",
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              color="info"
              mb={0}
              // display="block"
              className="dragHandle"
              lineHeight="1rem"
            >
              {widgetOptions.title}
            </Typography>
            <Typography
              variant="h6"
              color="info"
              mb={0}
              lineHeight="1rem"
              sx={{
                fontSize: "0.75rem",
                color: "#888",
                // whiteSpace: "nowrap",
                // overflow: "hidden",
                // textOverflow: "ellipsis",
                // cursor: "default",
              }}
            >
              {widgetTitle || widgetOptions.kpiName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pl: "0.15rem",
            }}
          >
            {isCustomizable && (
              <Box
                sx={{
                  width: "100%",
                  my: "-2px",
                }}
              >
                <TooltipLight title="Customize this widget" placement="right" variant="outline">
                  <MoreHorizIcon
                    style={{ cursor: "pointer", float: "right" }}
                    onClick={() => handleOpenModal()}
                  />
                </TooltipLight>
              </Box>
            )}

            {periodDates.start && (
              <Box
                sx={{
                  fontSize: "20px",
                  display: "flex",
                  alignItems: "center",
                  mx: "-10px",
                }}
              >
                <ArrowLeft
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePeriods("prev")}
                  sx={{
                    mr: "-3px",
                  }}
                />
                <Typography variant="caption" color="info" mb={0} display="block">
                  {formatDatesForWidget(
                    widgetOptions.timePeriod,
                    periodDates,
                    loginUser.fiscalYearStart
                  )}
                </Typography>
                <ArrowRight
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePeriods("next")}
                  sx={{
                    ml: "-3px",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box>
          <List sx={{ width: "100%" }}>
            {leaderboardData.map((person, i) => (
              <React.Fragment key={person.userId}>
                <ListItem
                  sx={{
                    mb: "-10px",
                    justifyContent: "space-evenly",
                  }}
                >
                  <Box
                    sx={{
                      width: "30px",
                    }}
                  >
                    <ListItemAvatar
                      sx={{
                        minWidth: "35px",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 26,
                          height: 26,
                          fontSize: "0.85rem",
                          bgcolor: getAvatarColor(i + 1),
                          border: "2px solid",
                          borderColor: getAvatarBorderColor(i + 1),
                        }}
                      >
                        {i + 1}
                      </Avatar>
                    </ListItemAvatar>
                  </Box>
                  <Box sx={{ overflow: "hidden", width: "70%" }}>
                    <TooltipLight title={person.userName} placement="right" variant="outline">
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: "300",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "default",
                          color: getUserNameColor(person.userId),
                        }}
                      >
                        {person.userName}
                      </Typography>
                    </TooltipLight>
                  </Box>
                  <Box sx={{ width: "30px" }}>
                    <ListItemText
                      primary={numberFormatter(person.count)}
                      sx={{ fontSize: "1.5rem", color: "#384765", float: "right" }}
                      disableTypography
                    />
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>
      <CustomizeLeaderboardModal
        open={open}
        handleClose={handleCloseModal}
        selectionListOptions={selectionListOptions}
        handleSave={handleModalSave}
        widgetOptions={widgetOptions}
      />
    </>
  );
}
